import { Link } from "react-router-dom";
import { InstagramLogo, YoutubeLogo, FacebookLogo, EnvelopeSimple } from "@phosphor-icons/react";
import { landingPages } from "@/data/landingPages";
import { eventGuides } from "@/data/eventGuides";
import { guides } from "@/data/guides";
import { HostBadges } from "@/components/site/HostBadges";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-emerald text-sand">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid md:grid-cols-3 gap-12 pb-16 border-b border-sand/15">
          <div>
            <p className="overline text-sage mb-4">The collection</p>
            <p className="font-display text-2xl leading-snug max-w-xs">
              Luxury short-term stays in Wilton Manors & Fort Lauderdale.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <p className="overline text-sage mb-1">Explore</p>
            <Link to="/about" className="hover:text-sunset transition-colors duration-300 w-fit">Why Us</Link>
            <Link to="/properties" className="hover:text-sunset transition-colors duration-300 w-fit">Stays</Link>
            <Link to="/neighborhood" className="hover:text-sunset transition-colors duration-300 w-fit">Guide</Link>
            <Link to="/events" className="hover:text-sunset transition-colors duration-300 w-fit">Events</Link>
            <Link to="/property-management" className="hover:text-sunset transition-colors duration-300 w-fit">Co-Hosting</Link>
            <Link to="/social" className="hover:text-sunset transition-colors duration-300 w-fit">Socials</Link>
            <Link to="/faq" className="hover:text-sunset transition-colors duration-300 w-fit">FAQ</Link>
            <Link to="/contact" className="hover:text-sunset transition-colors duration-300 w-fit">Contact</Link>
          </div>
          <div className="flex flex-col gap-4">
            <p className="overline text-sage mb-1">Say hello</p>
            <div className="flex gap-4">
              <a aria-label="Instagram" data-testid="footer-instagram" href="https://instagram.com/doublerainbowsuites" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-sand/25 grid place-items-center hover:bg-sunset hover:border-sunset transition-colors duration-300">
                <InstagramLogo size={20} aria-hidden="true" />
              </a>
              <a aria-label="Facebook" data-testid="footer-facebook" href="https://www.facebook.com/people/Double-Rainbow-Suites/100078176427437/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-sand/25 grid place-items-center hover:bg-sunset hover:border-sunset transition-colors duration-300">
                <FacebookLogo size={20} aria-hidden="true" />
              </a>
              <a aria-label="YouTube" data-testid="footer-youtube" href="https://youtube.com/@doublerainbowsuites" target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-sand/25 grid place-items-center hover:bg-sunset hover:border-sunset transition-colors duration-300">
                <YoutubeLogo size={20} aria-hidden="true" />
              </a>
              <Link aria-label="Contact us by email" to="/contact" className="w-11 h-11 rounded-full border border-sand/25 grid place-items-center hover:bg-sunset hover:border-sunset transition-colors duration-300">
                <EnvelopeSimple size={20} aria-hidden="true" />
              </Link>
            </div>
            <a data-testid="footer-phone" href="tel:+19543325725" className="text-sand/80 hover:text-sunset transition-colors duration-300 mt-2 w-fit">(954) 332-5725</a>
            <p className="text-sand/60 text-sm">1425 NE 24th St, Wilton Manors, FL 33305</p>
          </div>
        </div>

        <HostBadges dark className="pt-12" />

        <nav aria-label="Local and event guides" data-testid="footer-guides" className="pt-12">
          <p className="overline text-sage mb-4">Local &amp; event guides</p>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sand/70 text-sm">
            {guides.map((g) => (
              <Link
                key={g.slug}
                to={`/${g.slug}`}
                data-testid={`footer-guide-${g.slug}`}
                className="hover:text-sunset transition-colors duration-300"
              >
                {g.shortTitle}
              </Link>
            ))}
            {eventGuides.map((g) => (
              <Link
                key={g.slug}
                to={`/events/${g.slug}`}
                data-testid={`footer-event-guide-${g.slug}`}
                className="hover:text-sunset transition-colors duration-300"
              >
                {g.shortTitle}
              </Link>
            ))}
          </div>
        </nav>

        <nav aria-label="Popular searches" data-testid="footer-popular-searches" className="pt-12">
          <p className="overline text-sage mb-4">Popular searches</p>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sand/70 text-sm">
            {landingPages.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`footer-landing-${p.slug}`}
                className="hover:text-sunset transition-colors duration-300"
              >
                {p.h1}
              </Link>
            ))}
          </div>
        </nav>

        <div className="pt-14">
          <h2 className="font-display text-[15vw] md:text-[11vw] leading-[0.85] tracking-tighter text-sand/95">
            Double Rainbow
          </h2>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-sand/50 text-sm">
          <p>© {new Date().getFullYear()} Double Rainbow Suites. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-sunset transition-colors duration-300">Privacy &amp; Cookies</Link>
            <p>Wilton Manors · Fort Lauderdale · Florida</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
